import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AccountAccessService } from '@modules/navigation/services/account-access.service';
import { LEFT_NAV_HAMBURGER_ICON_KEY, LEFT_NAV_ITEMS } from '../../data/left-nav.data';
import { LEFT_NAV_ICON_PATHS } from '../../data/left-nav-icons.data';
import { LeftNavItem, LeftNavSubItem } from '../../models/left-nav-item.model';
import { LeftNavState } from '../../models/left-nav-state.model';
import { SideNavItems, SideNavSection } from '@modules/navigation/models';
import { LeftNavStore } from '../../services/left-nav.store';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { filter, Subscription } from 'rxjs';

/**
 * Side navigation container shell.
 * Keeps existing selector and input contract stable for layout integration
 * while the new left navigation implementation is introduced in a later step.
 */
@Component({
    selector: 'sb-side-nav',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './side-nav.component.html',
    styleUrls: ['side-nav.component.scss'],
})
export class SideNavComponent implements OnInit, OnDestroy {
    @Input() sidenavStyle!: string;
    @Input() sideNavItems!: SideNavItems;
    @Input() sideNavSections!: SideNavSection[];

    @ViewChild('sideNavRoot', { static: true }) sideNavRoot!: ElementRef<HTMLElement>;
    @ViewChild('railContainer') railContainer?: ElementRef<HTMLElement>;
    @ViewChild('flyoutContainer') flyoutContainer?: ElementRef<HTMLElement>;

    readonly state$ = this.leftNavStore.state$;
    readonly menuItems: LeftNavItem[];
    readonly hamburgerIconPath = LEFT_NAV_ICON_PATHS[LEFT_NAV_HAMBURGER_ICON_KEY];
    readonly appVersion = computed(() => {
        const v = this.appSettingsStore.settings()?.appVersion;
        if (!v) return '';
        return [v.web, v.webApi, v.fastApi].filter(Boolean).join(', ');
    });
    flyoutTop = 0;
    private readonly routerEventsSubscription = new Subscription();

    constructor(
        private readonly leftNavStore: LeftNavStore,
        private readonly router: Router,
        private readonly accountAccessService: AccountAccessService,
        private readonly appSettingsStore: AppSettingsStore
    ) {
        this.menuItems = this.getVisibleMenuItems();
    }

    ngOnInit(): void {
        this.syncNavigationStateFromUrl(this.router.url);
        this.routerEventsSubscription.add(
            this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
                this.syncNavigationStateFromUrl(event.urlAfterRedirects);
            })
        );
    }

    ngOnDestroy(): void {
        this.routerEventsSubscription.unsubscribe();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.leftNavStore.closeFlyout();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as Node | null;
        const state = this.leftNavStore.state;
        if (!target || !state.openedFlyoutModuleId) {
            return;
        }
        if (!this.sideNavRoot.nativeElement.contains(target)) {
            this.leftNavStore.closeFlyout();
        }
    }

    onHamburgerSelected(): void {
        this.leftNavStore.toggleNavigationMode();
    }

    onRailItemSelected(item: LeftNavItem, event: MouseEvent): void {
        this.leftNavStore.setActiveItem(item.id, null);
        if (item.children?.length) {
            this.updateFlyoutTop(event);
            this.leftNavStore.openFlyout(item.id);
            setTimeout(() => this.clampFlyoutToRailBounds(), 0);
            return;
        }
        if (item.route) {
            void this.router.navigate([item.route]);
        }
        this.leftNavStore.closeFlyout();
    }

    onRailItemHovered(item: LeftNavItem, event: MouseEvent): void {
        if (!item.children?.length || this.leftNavStore.state.mode !== 'collapsed') {
            return;
        }

        if (this.leftNavStore.state.openedFlyoutModuleId !== item.id) {
            this.updateFlyoutTop(event);
            this.leftNavStore.openFlyout(item.id);
            setTimeout(() => this.clampFlyoutToRailBounds(), 0);
        }
    }

    onFlyoutChildSelected(item: LeftNavSubItem): void {
        const openedModuleId = this.leftNavStore.state.openedFlyoutModuleId;
        if (!openedModuleId) {
            return;
        }
        this.leftNavStore.setActiveItem(openedModuleId, item.id);
        if (item.route) {
            const queryParams: Record<string, string> = {};
            if (item.action) {
                queryParams.navAction = item.action;
            }
            if (item.mode) {
                queryParams.navMode = item.mode;
            }
            void this.router.navigate([item.route], {
                queryParams,
            });
        }
        this.leftNavStore.closeFlyout();
    }

    onTreeParentToggled(moduleId: string): void {
        this.leftNavStore.toggleTreeModule(moduleId);
    }

    onTreeParentSelected(moduleId: string): void {
        const module = this.menuItems.find((item) => item.id === moduleId);
        if (module?.route) {
            this.leftNavStore.setActiveItem(moduleId, null);
            void this.router.navigate([module.route]);
            return;
        }

        if (module?.children?.length) {
            this.leftNavStore.toggleTreeModule(moduleId);
        }
    }

    onTreeChildSelected(event: { moduleId: string; childId: string }): void {
        this.leftNavStore.setActiveItem(event.moduleId, event.childId);
        const module = this.menuItems.find((item) => item.id === event.moduleId);
        const child = module?.children?.find((item) => item.id === event.childId);
        if (child?.route) {
            const queryParams: Record<string, string> = {};
            if (child.action) {
                queryParams.navAction = child.action;
            }
            if (child.mode) {
                queryParams.navMode = child.mode;
            }
            void this.router.navigate([child.route], {
                queryParams,
            });
        }
    }

    getIconPath(item: LeftNavItem): string {
        return LEFT_NAV_ICON_PATHS[item.iconKey];
    }

    getFlyoutChildren(state: LeftNavState): LeftNavSubItem[] {
        const module = this.getOpenedFlyoutModule(state);
        return module?.children ?? [];
    }

    getFlyoutParentLabel(state: LeftNavState): string {
        return this.getOpenedFlyoutModule(state)?.label ?? '';
    }

    isModuleExpanded(state: LeftNavState, moduleId: string): boolean {
        return state.expandedModuleId === moduleId;
    }

    private getOpenedFlyoutModule(state: LeftNavState): LeftNavItem | undefined {
        if (!state.openedFlyoutModuleId) {
            return undefined;
        }
        return this.menuItems.find((item) => item.id === state.openedFlyoutModuleId);
    }

    private getVisibleMenuItems(): LeftNavItem[] {
        if (this.accountAccessService.isApiTenantUser()) {
            return [];
        }

        return LEFT_NAV_ITEMS.filter((item) => this.canDisplayMenuItem(item));
    }

    private canDisplayMenuItem(item: LeftNavItem): boolean {
        if (item.id === 'integration') {
            return this.accountAccessService.canAccessIntegration();
        }

        if (item.id === 'access') {
            return this.accountAccessService.canAccessApiKeys();
        }

        return true;
    }

    private updateFlyoutTop(event: MouseEvent): void {
        const clickedElement = event.currentTarget as HTMLElement | null;
        const sideNavElement = this.sideNavRoot?.nativeElement;
        if (!clickedElement || !sideNavElement) {
            this.flyoutTop = 0;
            return;
        }

        const clickedRect = clickedElement.getBoundingClientRect();
        const sideNavRect = sideNavElement.getBoundingClientRect();
        this.flyoutTop = Math.max(0, clickedRect.top - sideNavRect.top);
    }

    private clampFlyoutToRailBounds(): void {
        const sideNavElement = this.sideNavRoot?.nativeElement;
        const railElement = this.railContainer?.nativeElement;
        const flyoutElement = this.flyoutContainer?.nativeElement;
        if (!sideNavElement || !railElement || !flyoutElement) {
            return;
        }

        const sideNavRect = sideNavElement.getBoundingClientRect();
        const railRect = railElement.getBoundingClientRect();
        const railTopInSideNav = railRect.top - sideNavRect.top;
        const maxTop = Math.max(
            railTopInSideNav,
            railTopInSideNav + railElement.clientHeight - flyoutElement.offsetHeight - 6
        );
        this.flyoutTop = Math.min(Math.max(this.flyoutTop, railTopInSideNav), maxTop);
    }

    private syncNavigationStateFromUrl(url: string): void {
        const match = this.findNavigationMatch(url);
        if (!match) {
            return;
        }

        this.leftNavStore.setActiveItem(match.module.id, match.child?.id ?? null);
        this.leftNavStore.setExpandedTreeModule(match.child ? match.module.id : null);
    }

    private findNavigationMatch(
        url: string
    ): { module: LeftNavItem; child: LeftNavSubItem | null } | null {
        const parsedUrl = this.router.parseUrl(url);
        const path = url.split('?')[0].split('#')[0];
        const queryParams = parsedUrl.queryParams ?? {};

        for (const item of this.menuItems) {
            if (item.route && this.normalizeRoute(item.route) === this.normalizeRoute(path)) {
                return { module: item, child: null };
            }

            const matchingChild = item.children?.find((child) => this.isMatchingChildRoute(child, path, queryParams));
            if (matchingChild) {
                return { module: item, child: matchingChild };
            }
        }

        return null;
    }

    private isMatchingChildRoute(
        child: LeftNavSubItem,
        path: string,
        queryParams: Record<string, string | string[]>
    ): boolean {
        if (!child.route || this.normalizeRoute(child.route) !== this.normalizeRoute(path)) {
            return false;
        }

        if (child.action && queryParams['navAction'] !== child.action) {
            return false;
        }

        if (child.mode && queryParams['navMode'] !== child.mode) {
            return false;
        }

        return true;
    }

    private normalizeRoute(route: string): string {
        return route.replace(/\/+$/, '');
    }
}
